import type { Components } from "@mui/material/styles";

import type { Theme } from "../types";

function getContainedVars(color: string): Record<string, string> {
	return {
		"--Button-containedBg": `var(--mui-palette-${color}-main)`,
	};
}

function getOutlinedVars(color: string, dark: boolean): Record<string, string> {
	const vars = {
		"--Button-outlinedBorder": `var(--mui-palette-${color}-main)`,
		"--Button-outlinedHoverBg": `var(--mui-palette-${color}-hovered)`,
		"--Button-outlinedActiveBg": `var(--mui-palette-${color}-activated)`,
	};

	// Secondary outlined uses lighter/darker border shades (200 light, 700 dark)
	// instead of main, because secondary.main is too dark for a subtle outline in light mode
	// and too light in dark mode — matching the design tokens' intent for secondary surfaces.
	if (color === "secondary") {
		vars["--Button-outlinedBorder"] = dark
			? "var(--mui-palette-secondary-700)"
			: "var(--mui-palette-secondary-200)";
	}

	return vars;
}

function getTextVars(color: string): Record<string, string> {
	return {
		"--Button-textHoverBg": `var(--mui-palette-${color}-hovered)`,
		"--Button-textActiveBg": `var(--mui-palette-${color}-activated)`,
	};
}

export const MuiButton = {
	defaultProps: { disableRipple: true },
	styleOverrides: {
		root: ({ theme }) => ({
			borderRadius: "8px",
			minHeight: "var(--Button-minHeight)",
			minWidth: "unset",
			textTransform: "none",
			"&:focus-visible": {
				outline: "2px solid var(--mui-palette-primary-main)",
			},
			"&.Mui-disabled": {
				opacity: 0.38,
				cursor: "not-allowed",
				pointerEvents: "auto", // Allow cursor change
				color: theme.palette.text.disabled,
				"&:hover": {
					// Remove all hover effects on disabled buttons
					backgroundColor: "transparent",
					boxShadow: "none",
				},
			},
		}),
		text: ({ theme }) => ({
			"&:hover": { backgroundColor: "var(--Button-textHoverBg)" },
			"&:active": { backgroundColor: "var(--Button-textActiveBg)" },
			"&.Mui-disabled": {
				backgroundColor: "transparent",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor: "transparent",
				},
			},
		}),
		textPrimary: getTextVars("primary"),
		textSecondary: getTextVars("secondary"),
		textSuccess: getTextVars("success"),
		textInfo: getTextVars("info"),
		textWarning: getTextVars("warning"),
		textError: getTextVars("error"),
		outlined: ({ theme }) => ({
			boxShadow: "var(--mui-shadows-1)",
			borderColor: "var(--Button-outlinedBorder)",
			"&:hover": {
				borderColor: "var(--Button-outlinedBorder)",
				backgroundColor: "var(--Button-outlinedHoverBg)",
			},
			"&:active": { backgroundColor: "var(--Button-outlinedActiveBg)" },
			"&.Mui-disabled": {
				backgroundColor: "transparent",
				borderColor: theme.palette.text.disabled,
				boxShadow: "none",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor: "transparent",
					borderColor: theme.palette.text.disabled,
					boxShadow: "none",
				},
			},
		}),
		outlinedPrimary: ({ theme }) => {
			return getOutlinedVars("primary", theme.palette.mode === "dark");
		},
		outlinedSecondary: ({ theme }) => {
			return getOutlinedVars("secondary", theme.palette.mode === "dark");
		},
		outlinedSuccess: ({ theme }) => {
			return getOutlinedVars("success", theme.palette.mode === "dark");
		},
		outlinedInfo: ({ theme }) => {
			return getOutlinedVars("info", theme.palette.mode === "dark");
		},
		outlinedWarning: ({ theme }) => {
			return getOutlinedVars("warning", theme.palette.mode === "dark");
		},
		outlinedError: ({ theme }) => {
			return getOutlinedVars("error", theme.palette.mode === "dark");
		},
		contained: ({ theme }) => ({
			backgroundColor: "var(--Button-containedBg)",
			boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
			"&:hover": {
				boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.15)",
				filter: "brightness(0.92)",
			},
			"&:focus-visible": {
				boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.15)",
				outlineOffset: "1px",
			},
			"&.Mui-disabled": {
				backgroundColor:
					theme.palette.action.disabledBackground || theme.palette.grey[100],
				boxShadow: "none",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor:
						theme.palette.action.disabledBackground || theme.palette.grey[100],
					boxShadow: "none",
				},
			},
		}),
		containedPrimary: getContainedVars("primary"),
		containedSecondary: getContainedVars("secondary"),
		containedSuccess: getContainedVars("success"),
		containedInfo: getContainedVars("info"),
		containedWarning: getContainedVars("warning"),
		containedError: getContainedVars("error"),
		sizeSmall: {
			"--Button-minHeight": "32px",
			fontSize: "0.8125rem",
			lineHeight: "24px",
		},
		sizeMedium: {
			"--Button-minHeight": "40px",
			fontSize: "0.875rem",
			lineHeight: "28px",
		},
		sizeLarge: {
			"--Button-minHeight": "48px",
			fontSize: "0.9375rem",
			lineHeight: "32px",
		},
	},
} satisfies Components<Theme>["MuiButton"];
